import {
  hasOpenOverlayInDocument,
  isDocumentRootFocus,
} from '@client/helpers/dom.helpers';
import {
  isOverlayEvent,
  shouldIgnoreKeyboardShortcut,
  shouldIgnoreUndoRedoShortcut,
} from '@shared/helpers/event.helpers';
import { expressionPanel } from '@features/expressions/ExpressionInputPanel/ExpressionInputPanel';
import {
  isElementExpanded,
  isExpandedItemsByKey,
  isGroupExpandedByKey,
  setElementExpanded,
  setExpandedItemsByKey,
  setGroupExpandedByKey,
} from '@client/hooks/use-expand-elements.hooks';
import type { useFlatSortFilteredElements } from '@client/hooks/use-filter-elements.hooks';
import {
  elementListUrlPageFromPage,
  type useProjectNavigation,
} from '@client/hooks/use-router.hooks';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { Page } from '@client/store/ui/ui.model';
import type { Baseline } from '@shared/models/project.model';
import {
  applyElementSelectionForProposalNavigation,
  resolveElementSelectionForProposalNavigation,
} from '@features/proposals/helpers/proposal-selection.helpers';
import type { IProposal } from '@shared/models/proposals.model';
import type { Action } from '@shared/helpers/pipeline/update-engine.models';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import {
  useEffect,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type {
  IProductListCategoryGroupView,
  IProductListGroupView,
} from '@features/products/product-list.helpers';
import type { OneOfListElements } from '@shared/models/project.model';
import type { GroupSectionData } from '@ui/ElementList/list.model';
import type { GroupSectionMeta } from '@features/elements/element-grouping.model';
import type { ImportListKeyboardNavigationContext } from '@features/csv-import/use-import-list-visible-row-ids.hook';
import { setCsvImportFileExpanded } from '@features/csv-import/import-list-expand.helpers';
import {
  CSV_IMPORT_EXPAND_PREFIX,
  isImportListGroupRowId,
  resolveImportCandidateRowIdForDiscard,
  resolveImportListNavSelectionId,
} from '@features/csv-import/import-list-navigation.helpers';
import {
  clearListSelection,
  setListSelection,
} from '@features/csv-import/list-selection.store';
import { resolveSelectToEdge } from '@features/csv-import/list-multi-select.helpers';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import {
  getNextActionListRowId,
  getNextProposalSelectionId,
  getNextVisibleListRowId,
  getVerticalArrowNavigationDirection,
  isSupportedKeyboardNavigationKey,
} from './element-list-keyboard-navigation.helpers';

export type ElementListTab = 'elements' | 'products' | 'actions' | 'imports';

const listPageFromTab = (tab: ElementListTab) =>
  elementListUrlPageFromPage(
    tab === 'products' ? Page.Products : Page.Elements,
  );

type ProjectNavigation = ReturnType<typeof useProjectNavigation>;
type FlatElements = ReturnType<typeof useFlatSortFilteredElements>;
type ElementGroup = GroupSectionData<OneOfListElements, GroupSectionMeta>;

type ElementLookup = {
  ancestorIdsByElementId: Map<string, string[]>;
};

type UseElementListKeyboardNavigationOptions = {
  selectedGroupKey: string | null;
  selectedElementId?: string;
  filteredFlatElements: FlatElements;
  elementLookup: ElementLookup;
  hasElementGroups: boolean;
  sortedElementGroups: ElementGroup[];
  forceExpandedGroupKeys: Set<string>;
  setSelectedGroupKey: Dispatch<SetStateAction<string | null>>;
  navigateTo: ProjectNavigation;
  listTab: ElementListTab;
  groupByCategory: boolean;
  sortedProductCategoryGroups: IProductListCategoryGroupView[];
  sortedProductGroups: IProductListGroupView[];
  proposals: IProposal[];
  proposalActions: Action[];
  selectedVersion: Baseline | undefined;
  selectedProposalId?: string;
  setSelectedProposalId: (id: string | undefined) => void;
  openSearch: () => void;
  handleTabChange: (tab: ElementListTab) => void;
  openFocusElement: (id: string) => void;
  openFocusGroup: (key: string) => void;
  focusViewBack: () => void;
  importListNavigation: ImportListKeyboardNavigationContext;
  /** Backspace/Delete: unmap if mapped, otherwise skip/restore. */
  toggleImportCandidateRowDiscard: (rowId: string) => void;
};

/**
 * Wires global keyboard shortcuts for the element list, product list, and actions list.
 * The document listener reads from a render-synced ref so it stays registered once.
 */
export const useElementListKeyboardNavigation = ({
  selectedGroupKey,
  selectedElementId,
  filteredFlatElements,
  elementLookup,
  hasElementGroups,
  sortedElementGroups,
  forceExpandedGroupKeys,
  setSelectedGroupKey,
  navigateTo,
  listTab,
  groupByCategory,
  sortedProductCategoryGroups,
  sortedProductGroups,
  proposals,
  proposalActions,
  selectedVersion,
  selectedProposalId,
  setSelectedProposalId,
  openSearch,
  handleTabChange,
  openFocusElement,
  openFocusGroup,
  focusViewBack,
  importListNavigation,
  toggleImportCandidateRowDiscard,
}: UseElementListKeyboardNavigationOptions): void => {
  const proposalActionIds = useMemo(
    () => proposalActions.map((a) => a.id),
    [proposalActions],
  );

  const arrowNavRef = useRef({
    selectedGroupKey,
    selectedElementId,
    filteredFlatElements,
    elementLookup,
    hasElementGroups,
    sortedElementGroups,
    forceExpandedGroupKeys,
    setSelectedGroupKey,
    navigateTo,
    listTab,
    proposalActionIds,
    groupByCategory,
    sortedProductCategoryGroups,
    sortedProductGroups,
    proposals,
    selectedVersion,
    selectedProposalId,
    setSelectedProposalId,
    openSearch,
    handleTabChange,
    openFocusElement,
    openFocusGroup,
    focusViewBack,
    importListNavigation,
    toggleImportCandidateRowDiscard,
  });

  arrowNavRef.current = {
    selectedGroupKey,
    selectedElementId,
    filteredFlatElements,
    elementLookup,
    hasElementGroups,
    sortedElementGroups,
    forceExpandedGroupKeys,
    setSelectedGroupKey,
    navigateTo,
    listTab,
    proposalActionIds,
    groupByCategory,
    sortedProductCategoryGroups,
    sortedProductGroups,
    proposals,
    selectedVersion,
    selectedProposalId,
    setSelectedProposalId,
    openSearch,
    handleTabChange,
    openFocusElement,
    openFocusGroup,
    focusViewBack,
    importListNavigation,
    toggleImportCandidateRowDiscard,
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (expressionPanel.isOpen()) return;

      const key = e.key;
      // Cmd/Ctrl+F → list search (Build / Products / Import), not browser find.
      const isFindShortcut =
        (e.metaKey || e.ctrlKey) &&
        !e.altKey &&
        !e.shiftKey &&
        (key === 'f' || key === 'F');
      if (isFindShortcut) {
        if (isOverlayEvent(e) || hasOpenOverlayInDocument()) return;
        const target = e.target;
        const inListSearch =
          target instanceof Element &&
          target.closest('[data-element-list-search]') !== null;
        if (!inListSearch && shouldIgnoreUndoRedoShortcut(e)) return;
        e.preventDefault();
        e.stopPropagation();
        arrowNavRef.current.openSearch();
        return;
      }

      if (shouldIgnoreKeyboardShortcut(e)) return;
      if (!isSupportedKeyboardNavigationKey(key)) return;

      if (key === KeyboardKeys.BACKSPACE || key === KeyboardKeys.DELETE) {
        const { listTab: tab, selectedElementId: elementId } =
          arrowNavRef.current;
        if (tab !== 'imports') return;
        if (e.metaKey || e.ctrlKey || e.altKey) return;

        const selectedId =
          elementId && elementId !== 'none' ? elementId : undefined;
        const {
          visibleRowIds,
          visibleImports,
          rowsByImport,
          importedElements,
        } = arrowNavRef.current.importListNavigation;
        const visibleSelectedId = resolveImportListNavSelectionId(
          selectedId,
          visibleRowIds,
          importedElements,
        );
        const rowId = resolveImportCandidateRowIdForDiscard(
          visibleSelectedId,
          visibleImports,
          rowsByImport,
          importedElements,
        );
        if (!rowId) return;

        e.preventDefault();
        e.stopPropagation();
        arrowNavRef.current.toggleImportCandidateRowDiscard(rowId);
        return;
      }

      if (key === 'Escape') {
        arrowNavRef.current.setSelectedGroupKey(null);
        return;
      }

      const {
        selectedGroupKey: groupKey,
        selectedElementId: elementId,
        filteredFlatElements: flatElements,
        elementLookup: lookup,
        hasElementGroups: hasGroups,
        sortedElementGroups: groups,
        forceExpandedGroupKeys,
        setSelectedGroupKey: setGroupKey,
        navigateTo: nav,
        listTab: tab,
        groupByCategory: byCategory,
        sortedProductCategoryGroups: productCatGroups,
        sortedProductGroups: productGroups,
        proposals: proposals,
        selectedProposalId: currentProposalId,
        setSelectedProposalId: setProposalId,
        openSearch: triggerOpenSearch,
        handleTabChange: changeTab,
      } = arrowNavRef.current;

      // --- E / P / S: tab navigation and search ---
      if (key === 'e' || key === 'E') {
        e.preventDefault();
        changeTab('elements');
        return;
      }
      if (key === 'p' || key === 'P') {
        if (e.shiftKey) return;
        e.preventDefault();
        changeTab('products');
        return;
      }
      if (key === 's' || key === 'S') {
        e.preventDefault();
        triggerOpenSearch();
        return;
      }

      // --- Enter / Shift+Enter: open / close focus view ---
      if (key === 'Enter') {
        e.preventDefault();
        const {
          openFocusElement: focusEl,
          openFocusGroup: focusGrp,
          focusViewBack: goBack,
        } = arrowNavRef.current;
        if (e.shiftKey) {
          goBack();
        } else {
          const activeElementId =
            elementId && elementId !== 'none' ? elementId : undefined;
          if (groupKey) focusGrp(groupKey);
          else if (activeElementId) focusEl(activeElementId);
        }
        return;
      }

      // --- 0-9: jump directly to baseline (0) or proposal by index (1-9) ---
      const isDigit = key.length === 1 && key >= '0' && key <= '9';
      if (isDigit) {
        e.preventDefault();
        const {
          listTab: tab,
          selectedVersion: version,
          selectedElementId: elementId,
          navigateTo: nav,
          setSelectedProposalId: setProposal,
        } = arrowNavRef.current;
        const digit = parseInt(key, 10);
        const targetProposalId =
          digit === 0 ? undefined : proposals[digit - 1]?.id;
        if (digit !== 0 && !targetProposalId) return;
        setProposal(targetProposalId);
        applyElementSelectionForProposalNavigation(
          resolveElementSelectionForProposalNavigation(
            version,
            targetProposalId,
            elementId,
          ),
          nav,
          listPageFromTab(tab),
        );
        return;
      }

      // --- Tab: cycle proposals only when no element has focus (body/html) ---
      if (e.key === 'Tab') {
        if (
          proposals.length > 0 &&
          isDocumentRootFocus(document.activeElement)
        ) {
          e.preventDefault();
          const direction = e.shiftKey ? 'previous' : 'next';
          const {
            listTab: tab,
            selectedVersion: version,
            selectedElementId: elementId,
            navigateTo: nav,
          } = arrowNavRef.current;
          const nextProposalId = getNextProposalSelectionId(
            proposals,
            currentProposalId,
            direction,
          );
          arrowNavRef.current.selectedProposalId = nextProposalId;
          setProposalId(nextProposalId);
          applyElementSelectionForProposalNavigation(
            resolveElementSelectionForProposalNavigation(
              version,
              nextProposalId,
              elementId,
            ),
            nav,
            listPageFromTab(tab),
          );
        }
        return;
      }

      const resolvedElementId =
        elementId && elementId !== 'none' ? elementId : undefined;

      // --- Imports tab: same arrow / expand semantics as elements & products ---
      if (tab === 'imports') {
        const {
          visibleRowIds,
          visibleImports,
          rowsByImport,
          importedElements,
        } = arrowNavRef.current.importListNavigation;
        const currentId = resolveImportListNavSelectionId(
          resolvedElementId,
          visibleRowIds,
          importedElements,
        );

        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          if (!currentId) return;
          if (!isImportListGroupRowId(currentId, visibleImports)) return;
          const expand = e.key === 'ArrowRight';
          const allImportIds = visibleImports.map((imp) => imp.id);
          const isExpanded = isExpandedItemsByKey(
            CSV_IMPORT_EXPAND_PREFIX,
            currentId,
          );
          if (isExpanded !== expand) {
            e.preventDefault();
            setCsvImportFileExpanded(allImportIds, currentId, expand);
          }
          return;
        }

        // Cmd/Ctrl+Shift+Arrow: select from the active row to the import edge.
        const metaOrCtrl = e.metaKey || e.ctrlKey;
        if (
          metaOrCtrl &&
          e.shiftKey &&
          (e.key === 'ArrowDown' || e.key === 'ArrowUp')
        ) {
          if (!currentId || isImportListGroupRowId(currentId, visibleImports)) {
            return;
          }
          const selectableIds = visibleRowIds.filter(
            (id) => !isImportListGroupRowId(id, visibleImports),
          );
          const importIdByRowId = new Map<string, string>();
          for (const [importId, rows] of Object.entries(rowsByImport)) {
            for (const row of rows) importIdByRowId.set(row.id, importId);
          }
          const next = resolveSelectToEdge(
            selectableIds,
            currentId,
            e.key === 'ArrowDown' ? 'down' : 'up',
            (rowId) => importIdByRowId.get(rowId),
          );
          if (!next) return;
          e.preventDefault();
          e.stopPropagation();
          arrowNavRef.current.selectedGroupKey = null;
          arrowNavRef.current.selectedElementId = next.edgeId;
          setGroupKey(null);
          setListSelection({
            selectedIds: next.selectedIds,
            anchorId: next.anchorId,
          });
          nav({ elementId: next.edgeId });
          return;
        }

        // Plain arrows ignore modifiers other than the gesture above.
        if (metaOrCtrl || e.shiftKey || e.altKey) return;

        const direction = getVerticalArrowNavigationDirection(e.key);
        if (!direction && !(e.key === 'ArrowDown' && !currentId)) return;

        const nextId = getNextVisibleListRowId({
          visibleIds: visibleRowIds,
          selectedId: currentId,
          direction: direction ?? 'next',
        });
        if (!nextId) return;
        e.preventDefault();
        arrowNavRef.current.selectedGroupKey = null;
        arrowNavRef.current.selectedElementId = nextId;
        setGroupKey(null);
        // Plain arrow moves the single active row and collapses multi-select.
        if (isImportListGroupRowId(nextId, visibleImports)) {
          clearListSelection();
        } else {
          setListSelection({
            selectedIds: new Set([nextId]),
            anchorId: nextId,
          });
        }
        nav({ elementId: nextId });
        return;
      }

      const currentId = groupKey ?? resolvedElementId;

      // --- Actions tab: vertical arrows move along proposal actions only ---
      if (tab === 'actions') {
        const direction = getVerticalArrowNavigationDirection(e.key);
        if (!direction) return;
        const nextId = getNextActionListRowId({
          actionIds: arrowNavRef.current.proposalActionIds,
          selectedId: resolvedElementId,
          direction,
        });
        if (!nextId) return;
        e.preventDefault();
        arrowNavRef.current.selectedGroupKey = null;
        arrowNavRef.current.selectedElementId = nextId;
        setGroupKey(null);
        nav({ elementId: nextId });
        return;
      }

      // --- ArrowDown with nothing selected: select first visible row ---
      if (!currentId && e.key === 'ArrowDown') {
        // visibleIds will be built below — reuse the same logic by falling
        // through with a sentinel that won't match any real id, then pick [0].
      } else if (!currentId) {
        return;
      }

      // --- Collapse / expand (ArrowLeft / ArrowRight) ---
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && currentId) {
        const expand = e.key === 'ArrowRight';

        if (tab === 'products') {
          const isCatGroup = productCatGroups.some((g) => g.id === currentId);
          const prefix = isCatGroup ? 'product-category' : 'product-group';
          if (isExpandedItemsByKey(prefix, currentId) !== expand) {
            e.preventDefault();
            setExpandedItemsByKey(prefix, currentId, expand);
          }
        } else {
          const isGroup = groups.some((g) => g.key === currentId);
          if (isGroup) {
            if (isGroupExpandedByKey(currentId) !== expand) {
              e.preventDefault();
              setGroupExpandedByKey(currentId, expand);
            }
          } else {
            if (isElementExpanded(currentId) !== expand) {
              e.preventDefault();
              setElementExpanded(currentId, expand);
            }
          }
        }
        return;
      }

      // --- Build visible ordered list (ArrowUp / ArrowDown) ---
      const visibleIds: string[] = [];

      if (tab === 'products') {
        if (byCategory) {
          for (const catGroup of productCatGroups) {
            visibleIds.push(catGroup.id);
            if (isExpandedItemsByKey('product-category', catGroup.id)) {
              for (const pg of catGroup.visibleElements) {
                visibleIds.push(pg.id);
                if (isExpandedItemsByKey('product-group', pg.id)) {
                  for (const item of pg.visibleElements) {
                    visibleIds.push(item.id);
                  }
                }
              }
            }
          }
        } else {
          for (const pg of productGroups) {
            visibleIds.push(pg.id);
            if (isExpandedItemsByKey('product-group', pg.id)) {
              for (const item of pg.visibleElements) {
                visibleIds.push(item.id);
              }
            }
          }
        }
      } else {
        // Build children map for elements (BFS order preserves sibling order)
        const childrenByParentId = new Map<string | undefined, string[]>();
        for (const el of flatElements) {
          if (!projectGuards.is.element(el)) continue;
          const ancestors = lookup.ancestorIdsByElementId.get(el.id) ?? [];
          const parentId = ancestors[ancestors.length - 1];
          let arr = childrenByParentId.get(parentId);
          if (!arr) {
            arr = [];
            childrenByParentId.set(parentId, arr);
          }
          arr.push(el.id);
        }

        const visitElement = (id: string) => {
          visibleIds.push(id);
          if (isElementExpanded(id)) {
            for (const childId of childrenByParentId.get(id) ?? []) {
              visitElement(childId);
            }
          }
        };

        if (hasGroups) {
          for (const group of groups) {
            visibleIds.push(group.key);
            const groupExpanded =
              forceExpandedGroupKeys.has(group.key) ||
              isGroupExpandedByKey(group.key);
            if (groupExpanded) {
              for (const el of group.items.filter(projectGuards.is.element)) {
                visitElement(el.id);
              }
            }
          }
        } else {
          for (const id of childrenByParentId.get(undefined) ?? []) {
            visitElement(id);
          }
        }
      }

      if (visibleIds.length === 0) return;

      // Nothing selected + ArrowDown → jump straight to first row
      if (!currentId) {
        e.preventDefault();
        const firstId = visibleIds[0];
        if (!firstId) return;
        const isGroupFirst =
          tab !== 'products' && groups.some((g) => g.key === firstId);
        if (isGroupFirst) {
          arrowNavRef.current.selectedGroupKey = firstId;
          arrowNavRef.current.selectedElementId = undefined;
          setGroupKey(firstId);
          if (elementId && elementId !== URL_CATEGORY_NONE) {
            nav({ elementId: URL_CATEGORY_NONE });
          }
        } else {
          arrowNavRef.current.selectedGroupKey = null;
          arrowNavRef.current.selectedElementId = firstId;
          setGroupKey(null);
          nav({ elementId: firstId });
        }
        return;
      }

      const currentIndex = visibleIds.indexOf(currentId);
      if (currentIndex === -1) return;

      const nextIndex =
        e.key === 'ArrowDown'
          ? Math.min(currentIndex + 1, visibleIds.length - 1)
          : Math.max(currentIndex - 1, 0);

      if (nextIndex === currentIndex) return;
      e.preventDefault();

      const nextId = visibleIds[nextIndex];
      if (!nextId) return;
      const isGroup =
        tab !== 'products' && groups.some((g) => g.key === nextId);
      if (isGroup) {
        arrowNavRef.current.selectedGroupKey = nextId;
        arrowNavRef.current.selectedElementId = undefined;
        setGroupKey(nextId);
        if (elementId && elementId !== URL_CATEGORY_NONE) {
          nav({ elementId: URL_CATEGORY_NONE });
        }
      } else {
        arrowNavRef.current.selectedGroupKey = null;
        arrowNavRef.current.selectedElementId = nextId;
        setGroupKey(null);
        nav({ elementId: nextId });
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);
};
